import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedImageBlob } from '../../lib/cropImage';

/** Aspect ratio used by collaboration highlight cards on the public profile (aspect-video = 16:9) */
export const COLLAB_THUMBNAIL_ASPECT = 16 / 9;

/** Output dimensions for cropped thumbnail (16:9) */
const CROP_OUTPUT_WIDTH = 640;
const CROP_OUTPUT_HEIGHT = 360;

/** Smaller size for live preview in modal */
const PREVIEW_WIDTH = 320;
const PREVIEW_HEIGHT = 180;

/**
 * Modal cropper for collaboration thumbnails. Fixed 16:9 aspect, zoom slider, Cancel/Save.
 * Shows a preview frame matching the profile card (aspect-video, rounded-md).
 */
function CollaborationThumbnailCropModal({
  isOpen,
  imageSrc,
  onCancel,
  onSave,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const previewTimeoutRef = useRef(null);
  const lastPreviewRef = useRef(null);

  const handleCropComplete = useCallback((_, pixels) => {
    if (pixels) setCroppedAreaPixels(pixels);
  }, []);

  // Debounced live preview: when crop area changes, generate a small preview image after a short delay
  useEffect(() => {
    if (!imageSrc || !croppedAreaPixels) {
      setPreviewUrl(null);
      return;
    }
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    previewTimeoutRef.current = setTimeout(async () => {
      try {
        const blob = await getCroppedImageBlob(
          imageSrc,
          croppedAreaPixels,
          PREVIEW_WIDTH,
          PREVIEW_HEIGHT
        );
        if (lastPreviewRef.current) {
          URL.revokeObjectURL(lastPreviewRef.current);
        }
        const url = URL.createObjectURL(blob);
        lastPreviewRef.current = url;
        setPreviewUrl(url);
      } catch {
        setPreviewUrl(null);
      }
      previewTimeoutRef.current = null;
    }, 200);
    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
  }, [imageSrc, croppedAreaPixels]);

  useEffect(() => {
    return () => {
      if (lastPreviewRef.current) {
        URL.revokeObjectURL(lastPreviewRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      const blob = await getCroppedImageBlob(
        imageSrc,
        croppedAreaPixels,
        CROP_OUTPUT_WIDTH,
        CROP_OUTPUT_HEIGHT
      );
      const croppedFile = new File([blob], 'thumbnail-cropped.jpg', { type: 'image/jpeg' });
      onSave({
        croppedFile,
        crop: {
          left: croppedAreaPixels.x,
          top: croppedAreaPixels.y,
          width: croppedAreaPixels.width,
          height: croppedAreaPixels.height,
        },
        aspect: COLLAB_THUMBNAIL_ASPECT,
      });
    } catch (err) {
      console.error('CollaborationThumbnailCropModal save error:', err);
      setSaveError(err.message || 'Failed to crop image. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    setSaveError(null);
    onCancel();
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-xl mx-4">
        <h2 className="text-base font-semibold text-slate-900">
          Crop thumbnail
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Thumbnail uses 16:9 on your profile. Drag to reposition and use the slider to zoom.
        </p>

        <div className="mt-4 relative h-52 w-full bg-slate-100 rounded-xl overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={COLLAB_THUMBNAIL_ASPECT}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-xs text-slate-500">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-slate-500 w-8 text-right">{zoom.toFixed(1)}x</span>
        </div>

        {/* Live preview matching profile card (aspect-video rounded-md) */}
        <p className="mt-3 text-xs text-slate-500">Preview (as on profile)</p>
        <div className="mt-1 aspect-video w-full max-w-[200px] rounded-md border border-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Thumbnail preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xs text-slate-400">Adjust crop above</span>
          )}
        </div>

        {saveError && (
          <p className="mt-2 text-xs text-red-600" role="alert">
            {saveError}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="text-sm font-medium text-slate-500 hover:text-slate-700 px-4 py-2 rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CollaborationThumbnailCropModal;
